import { Component, lazy, Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router'
import { ConnectedRouter } from 'connected-react-router'
import { connect } from 'react-redux'
import FullPageLoadingIcon from 'shared/FullPageLoadingIcon'
import Highcharts from "highcharts/highstock"
import { graphColors } from "shared/utilities"
import {
    isIndividualPlan,
    isInstitutionalPlan,
    isJobPostPlan,
    isOpenRemunerationPlan,
    isProfilePlan,
    isRealInstitutionalParentPlan,
    isStarterPlan,
    viewAdvancedFeatureVisible
} from 'shared/plans'
import { isJobApp } from "shared/apps"

import PrivateRoute from './components/PrivateRoute'
import ScrollToTop from 'member/components/ScrollToTop'
import ScrollUpButton from "react-scroll-up-button"
import MemberIndex from './index'

const Dashboard = lazy(() => import('./sections/Dashboard'))
const DirectorAnalysis = lazy(() => import('./sections/DirectorAnalysis'))
const DirectorRanking = lazy(() => import('./sections/DirectorRanking'))
const CompanyRanking = lazy(() => import('./sections/CompanyRanking'))
const ExecutiveRanking = lazy(() => import('./sections/ExecutiveRanking'))
const Bubbles = lazy(() => import('./sections/Bubbles'))
const Director = lazy(() => import('member/sections/Director'))
const Government = lazy(() => import('member/sections/Government'))
const MinorGovernment = lazy(() => import('member/sections/MinorGovernment'))
const ESGandRemuneration = lazy(() => import('member/sections/ESGandRemuneration'))
const RemunerationAnalytics = lazy(() => import('member/sections/RemunerationAnalytics'))
const Statistics = lazy(() => import('member/sections/Statistics'))
const SkillsDiversity = lazy(() => import('member/sections/SkillsDiversity'))
const SkillsDiversityDetail = lazy(() => import('member/sections/SkillsDiversityDetail'))
const DirectorsBoardCount = lazy(() => import('member/sections/DirectorsBoardCount'))
const CompanyDirectorShares = lazy(() => import('member/sections/CompanyDirectorShares'))
const Settings = lazy(() => import('member/sections/Settings'))
const UserReports = lazy(() => import('member/sections/UserReports'))
const PowerSearch = lazy(() => import('member/sections/PowerSearch'))
const Executive = lazy(() => import('member/sections/Executive'))
const CompanyExecutive = lazy(() => import('member/sections/CompanyExecutive'))
const SuperannuationExecutive = lazy(() => import('member/sections/SuperannuationExecutive'))
const GovernmentExecutive = lazy(() => import('member/sections/GovernmentExecutive'))
const MinorGovernmentExecutive = lazy(() => import('member/sections/MinorGovernmentExecutive'))
const ExecutiveRemuneration = lazy(() => import('member/sections/ExecutiveRemuneration'))
const BookmarkFolders = lazy(() => import('member/sections/BookmarkFolders'))
const ExecutiveChange = lazy(() => import('member/sections/ExecutiveChange'))
const ExecutiveRisk = lazy(() => import('member/sections/ExecutiveRisk'))
const CompanyRisk = lazy(() => import('member/sections/CompanyRisk'))
const ExecutiveDeparture = lazy(() => import('member/sections/ExecutiveDeparture'))
const NonExecDirectorRenewal = lazy(() => import('member/sections/NonExecDirectorRenewal'))
const NonExecDirectorChange = lazy(() => import('member/sections/NonExecDirectorChange'))
const PrivateCompanyExecutive = lazy(() => import('member/sections/PrivateCompanyExecutive'))
const Nfp = lazy(() => import('member/sections/Nfp'))
const NfpExecutive = lazy(() => import('member/sections/NfpExecutive'))
const DataVisualization = lazy(() => import("./sections/DataVisualization"))
const Connection = lazy(() => import("./sections/Connection"))
const Notifications = lazy(() => import("./sections/Notifications"))
const RemunerationComparison = lazy(() => import("./sections/RemunerationComparison"))
const RemunerationWorksheet = lazy(() => import("./sections/RemunerationWorksheet"))
const CommitteeMembership = lazy(() => import("./sections/CommitteeMembership"))
const RemCompare = lazy(() => import("./sections/RemCompare"))
const MemberDirector = lazy(() => import("./sections/MemberDirector"))
const UserManagement = lazy(() => import("./sections/UserManagement"))
const Position = lazy(() => import("./sections/Position"))
const OpenDashboard = lazy(() => import("./sections/OpenPeople/OpenDashboard"))
const Boards = lazy(() => import('./sections/Boards'))
const Company = lazy(() => import('member/sections/Company'))
const PrivateCompany = lazy(() => import('member/sections/PrivateCompany'))
const Superannuation = lazy(() => import('member/sections/Superannuation'))
const CeoRemunerationComponents = lazy(() => import('member/sections/CeoRemunerationComponents'))
const RemunerationPolicy = lazy(() => import('member/sections/RemunerationPolicy'))
const BoardPolicy = lazy(() => import('member/sections/BoardPolicy'))
const RemunerationInformation = lazy(() => import('member/sections/RemunerationInformation'))
const Organisations = lazy(() => import('member/sections/Organisations'))
const RiskExecutive = lazy(() => import('member/sections/RiskExecutive'))

const setGlobalOptions = () => {
    Highcharts.setOptions({
        lang: {
            numericSymbols: ["k", "M", "B", "T", "P", "E"],
            thousandsSep: ','
        },
        colors: graphColors()
    });
};

class Routes extends Component {
    componentDidMount() {
        setGlobalOptions()
    }

    render() {
        const { users: { user }, history } = this.props
        const institutionalUser = isInstitutionalPlan(user)
        const realInstitutionalParentUser = isRealInstitutionalParentPlan(user)

        const institutionalSingleUser = isIndividualPlan(user)
        const profileUser = isProfilePlan(user)
        const starterUser = isStarterPlan(user)

        const advancedFeature = viewAdvancedFeatureVisible(user)

        const validOpenRemSubscription = isOpenRemunerationPlan(user)
        const validOpenDirectorSubscription = institutionalUser || profileUser || institutionalSingleUser || starterUser
        const validJobPostSubscription = isJobPostPlan(user)

        const jobApp = isJobApp()

        return (
            <ConnectedRouter history={history}>
                <ScrollToTop>
                    <MemberIndex>
                        <Suspense fallback={<FullPageLoadingIcon />}>
                            <Switch>
                                <Route path="/root" component={Dashboard} />
                                <Route exact path="/home" component={Organisations} />
                                <Route exact path="/" render={() => {
                                    if (jobApp) {
                                        return (
                                            <OpenDashboard />
                                        )
                                    } else {
                                        if (gon.first_time) {
                                            if (profileUser) {
                                                return (
                                                    <Redirect to="/member-profile" />
                                                )
                                            } else if (realInstitutionalParentUser) {
                                                return (
                                                    <Redirect to="/member-user" />
                                                )
                                            }
                                        } else {
                                            return (
                                                <Organisations />
                                            )
                                        }
                                    }

                                }} />
                                <Route exact path="/boards" component={Boards} />
                                <Route exact path="/companies/:companyId" objectKey="companyId" component={Company} />
                                <Route exact path="/private-companies/:companyId" objectKey="privateCompanyId"
                                    component={PrivateCompany} />
                                <Route exact path="/superannuations/:superannuationId" objectKey="superannuationId"
                                    component={Superannuation} />
                                <Route exact path="/governments/:governmentId" objectKey="governmentId"
                                    component={Government} />
                                <Route exact path="/minor-governments/:minorGovernmentId" objectKey="minorGovernmentId"
                                    component={MinorGovernment} />
                                <Route exact path="/nfps/:nfpId" objectKey="nfpId" component={Nfp} />
                                <Route exact path="/bubbles" component={Bubbles} />
                                <Route exact path="/director-ranking" component={DirectorRanking} />
                                <Route exact path="/executive-ranking" component={ExecutiveRanking} />
                                <Route exact path="/company-ranking" component={CompanyRanking} />
                                <Route exact path="/executives" component={Executive} />
                                <Route exact path="/companies-with-executives/:companyId" component={CompanyExecutive} />
                                <Route exact path="/private-companies-with-executives/:companyId"
                                    component={PrivateCompanyExecutive} />
                                <Route exact path="/nfps-with-executives/:nfpId" component={NfpExecutive} />
                                <Route exact path="/superannuations-with-executives/:superannuationId"
                                    component={SuperannuationExecutive} />
                                <Route exact path="/governments-with-executives/:governmentId"
                                    component={GovernmentExecutive} />
                                <Route exact path="/minor-governments-with-executives/:minorGovernmentId"
                                    component={MinorGovernmentExecutive} />
                                <Route key='esg' exact path="/esg" component={ESGandRemuneration} />
                                <Route exact path="/director-analysis" component={DirectorAnalysis} />
                                <Route key='remuneration-analytics' exact path="/remuneration-analytics"
                                    component={RemunerationAnalytics} />
                                <Route exact path="/directors-board-count" component={DirectorsBoardCount} />
                                <Route key='remuneration' exact path="/board-remunerations"
                                    component={ESGandRemuneration} />
                                <Route key='executive-remuneration' exact path="/executive-remunerations"
                                    component={ExecutiveRemuneration} />
                                <Route key='statistics' exact path="/statistics" component={Statistics} />
                                <Route exact path="/company-director-shares" component={CompanyDirectorShares} />
                                <Route exact path="/skills-diversity" component={SkillsDiversity} />
                                <Route exact path="/skills-diversity/:skillDiversityId" objectKey="skillDiversityId"
                                    component={SkillsDiversityDetail} />
                                <Route exact path="/search" component={PowerSearch} />
                                <Route exact path="/executive-changes" component={ExecutiveChange} />
                                <Route exact path="/company-risks" component={CompanyRisk} />
                                <Route exact path="/non-executive-director-renewals"
                                    component={NonExecDirectorRenewal} />
                                <Route exact path="/non-executive-director-changes" component={NonExecDirectorChange} />
                                <Route exact path="/connections" component={Connection} />
                                <Route exact path="/committee-membership" component={CommitteeMembership} />
                                <Route exact path="/executive-departures" component={ExecutiveDeparture} />
                                <Route exact path="/executive-risks" component={ExecutiveRisk} />
                                <Route exact path="/data-viz" component={DataVisualization} />
                                <Route exact path="/rem-compare" component={RemCompare} />
                                <PrivateRoute exact path="/settings" component={Settings}
                                    authorized={validOpenDirectorSubscription || validOpenRemSubscription} />
                                <PrivateRoute exact path="/reports" component={UserReports}
                                    authorized={validOpenDirectorSubscription || validOpenRemSubscription} />
                                <PrivateRoute exact path="/directors/:directorId" component={Director}
                                    objectKey="directorId"
                                    authorized={validOpenDirectorSubscription || validOpenRemSubscription || validJobPostSubscription} />
                                <PrivateRoute exact path="/lists" component={BookmarkFolders}
                                    authorized={institutionalUser || institutionalSingleUser} />
                                <PrivateRoute exact path="/notifications" component={Notifications}
                                    authorized={institutionalUser || institutionalSingleUser} />
                                <PrivateRoute exact path="/positions" component={Position}
                                    authorized={institutionalUser || institutionalSingleUser || validJobPostSubscription} />
                                <PrivateRoute exact path="/remuneration-comparison" component={RemunerationComparison}
                                    authorized={validOpenRemSubscription} />
                                <PrivateRoute exact path="/remuneration-worksheet" component={RemunerationWorksheet}
                                    authorized={validOpenRemSubscription} />
                                <PrivateRoute exact path="/member-profile" component={MemberDirector}
                                    authorized={profileUser || institutionalUser || institutionalSingleUser} />
                                <PrivateRoute exact path="/member-user" component={UserManagement}
                                    authorized={realInstitutionalParentUser} />
                                <PrivateRoute exact path="/ceo-remuneration-components"
                                    component={CeoRemunerationComponents}
                                    authorized={institutionalUser || institutionalSingleUser || validOpenRemSubscription} />
                                <PrivateRoute exact path="/remuneration-policy" component={RemunerationPolicy}
                                    authorized={institutionalUser || institutionalSingleUser || validOpenRemSubscription} />
                                <PrivateRoute exact path="/board-policy" component={BoardPolicy}
                                    authorized={institutionalUser || institutionalSingleUser || validOpenRemSubscription} />
                                <PrivateRoute exact path="/remuneration-information" component={RemunerationInformation}
                                    authorized={validOpenRemSubscription} />
                                <Route exact path="/organisations" component={Organisations} />
                                <Route exact path="/executive-risk/:id" component={RiskExecutive} />
                            </Switch>
                        </Suspense>
                    </MemberIndex>
                    <ScrollUpButton
                        style={{
                            backgroundColor: '#35d1be',
                            bottom: 100,
                            width: 50,
                            borderRadius: 5,

                        }}
                    />
                </ScrollToTop>
            </ConnectedRouter>
        )
    }
}

function mapStateToProps(state) {
    const { users } = state;

    return {
        users
    }
}

export default connect(mapStateToProps, null)(Routes)

